// messages.component.ts
import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import { MessageApiService } from '@app/services/message-api.service';
import { MessageInterface } from '@app/interface/messages';
import { Subject, takeUntil } from 'rxjs';
import { ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BackHeaderComponent } from '@app/back-header/back-header.component';
import {
  MatBottomSheet,
  MatBottomSheetModule,
  MatBottomSheetRef,
} from '@angular/material/bottom-sheet';
import {
  MessageDetailBottomSheetComponent,
  MessageDetailData,
} from '@app/message-detail-bottom-sheet/message-detail-bottom-sheet.component';
import { MatChipListbox, MatChipOption } from '@angular/material/chips';

@Component({
  selector: 'izy-messages',
  templateUrl: './messages.component.html',
  styleUrls: ['./messages.component.scss'],
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatDividerModule,
    MatListModule,
    BackHeaderComponent,
    // MatChipOption,
  ],
})
export class MessagesComponent implements OnInit, OnDestroy {
  messages: MessageInterface[] = [];
  loading = true;
  error: string | null = null;
  private destroy$ = new Subject<void>();
  toReadMessages: number = 0;

  // Filter states
  activeFilters = {
    withAttachment: false,
    unread: false,
  };

  constructor(
    private messageApiService: MessageApiService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    private bottomSheet: MatBottomSheet,
  ) {}

  ngOnInit(): void {
    this.loadMessages();

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadMessages(): void {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    this.loading = true;
    this.error = null;
    this.cdr.detectChanges();

    // Prepare filter params
    const filterParams: any = {};
    filterParams.is_deleted = false;

    // Apply active filters to API call
    if (this.activeFilters.withAttachment) {
      filterParams.has_attachment = true;
    }
    if (this.activeFilters.unread) {
      filterParams.is_read = false;
    }

    filterParams.ordering = '-id';

    this.messageApiService
      .getAllMessages<MessageInterface>(organization_id, filterParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Filter and map messages
          this.messages = response.results;
          // .sort((a, b) => {
          //   const dateA = new Date(a.date || a.created_at || 0).getTime();
          //   const dateB = new Date(b.date || b.created_at || 0).getTime();
          //   return dateB - dateA; // Descending order
          // });

          // Count unread messages
          this.toReadMessages = this.messages.filter(
            (message) => message.is_read === false,
          ).length;

          this.loading = false;

          this.cdr.detectChanges(); // Force change detection after data
        },
        error: (error) => {
          this.error = 'Errore durante il caricamento';
          this.loading = false;
          console.error('Error loading messages:', error);

          this.cdr.detectChanges(); // Force change detection after data
        },
      });
  }

  openMessageDetail(message: MessageInterface): void {
    const data: MessageDetailData = { message };

    this.bottomSheet.open(MessageDetailBottomSheetComponent, {
      data,
      panelClass: 'message-detail-bottom-sheet',
    });

    this.toggleReadStatus(message);

    this.cdr.detectChanges();
  }

  toggleReadStatus(message: MessageInterface): void {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    this.cdr.detectChanges();

    this.messageApiService
      .updateMessage(organization_id, message.id, {
        is_read: true,
      })
      .subscribe({
        next: (updatedMessage: any) => {
          // Update the local data
          const index = this.messages.findIndex((i) => i.id === message.id);

          this.cdr.detectChanges();
        },
        error: (err: any) => {
          console.error('Error updating message:', err);
          // Revert the checkbox if the update fails
          message.is_read = false;

          this.cdr.detectChanges();
        },
      });
  }

  truncateText(text: string, limit: number = 100): string {
    if (!text) return '';
    return text.length > limit ? text.substring(0, limit) + '...' : text;
  }

  clearFilters(): void {
    this.activeFilters = {
      withAttachment: false,
      unread: false,
    };
    this.loadMessages(); // Reload without filters
  }

  // Check if any filter is active
  get hasActiveFilters(): boolean {
    return Object.values(this.activeFilters).some((value) => value);
  }

  // toggleAttachmentFilter(): void {
  //   this.activeFilters.withAttachment = !this.activeFilters.withAttachment;
  //   this.loadMessages(); // Reload with new filters
  // }

  toggleUnreadFilter(): void {
    this.activeFilters.unread = !this.activeFilters.unread;
    this.loadMessages(); // Reload with new filters
    this.cdr.detectChanges();
  }

  getMessageTypeColor(type: 'IN' | 'OUT'): string {
    return type === 'IN' ? 'primary' : 'accent';
  }

  getMessageTypeIcon(type: 'IN' | 'OUT'): string {
    return type === 'IN' ? 'mail' : 'upload';
  }

  getMessageTypeLabel(type: 'IN' | 'OUT'): string {
    return type === 'IN' ? 'In arrivo' : 'Inviato';
  }

  hasAttachment(message: MessageInterface): boolean {
    return !!message.attached_file;
  }

  downloadAttachment(message: MessageInterface): void {
    if (message.attached_file) {
      // Implement download logic here
      window.open(message.attached_file, '_blank');
    }
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString();
  }

  backToDashboard(): void {
    const org_id = this.route.snapshot.paramMap.get('organizationId');
    this.router.navigate([`/${org_id}/dashboard`]);
  }
}
